import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { rechargeAPI, walletAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import BannerSlider from '../components/BannerSlider';
import {
  Smartphone, Loader2, CheckCircle, XCircle, Clock, IndianRupee,
  AlertTriangle, Tv, Zap, Search, MapPin, Info, Wallet,
  ArrowRight, Sparkles, Signal, Eye, Gift, RotateCcw, Send, RefreshCw, User, Copy,
} from 'lucide-react';
import { format } from 'date-fns';

const CIRCLES = [
  'Andhra Pradesh Telangana', 'Assam', 'Bihar Jharkhand', 'Chennai',
  'Delhi NCR', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu Kashmir',
  'Karnataka', 'Kerala', 'Kolkata', 'Madhya Pradesh Chhattisgarh',
  'Maharashtra Goa', 'Mumbai', 'North East', 'Orissa', 'Punjab',
  'Rajasthan', 'Tamil Nadu', 'UP East', 'UP West', 'West Bengal',
];

const Recharge = () => {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState({ prepaid: [], dth: [], electricity: [], google_play: [] });
  const [livePlans, setLivePlans] = useState([]);
  const [liveOffers, setLiveOffers] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [planTab, setPlanTab] = useState('all');
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [offersOpen, setOffersOpen] = useState(false);
  const [recentRecharges, setRecentRecharges] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [dthInfo, setDthInfo] = useState(null);
  const [loadingDthInfo, setLoadingDthInfo] = useState(false);
  const [dthInfoOpen, setDthInfoOpen] = useState(false);
  const [refreshingDth, setRefreshingDth] = useState(false);
  const [commRates, setCommRates] = useState(null);

  const defaultType = searchParams.get('type') || 'prepaid';
  const [rechargeType, setRechargeType] = useState(defaultType);

  const [formData, setFormData] = useState({
    mobile: '', operator: '', circle: 'Delhi NCR', amount: '',
  });

  useEffect(() => { fetchOperators(); fetchRecent(); }, []);

  useEffect(() => {
    if (formData.operator && formData.circle && (rechargeType === 'prepaid' || rechargeType === 'postpaid')) {
      fetchLivePlans(formData.operator, formData.circle);
    }
    if (formData.operator && rechargeType === 'dth') {
      fetchDthPlans(formData.operator);
    }
    // Fetch commission rates for operator
    if (formData.operator) {
      fetchCommissionRates(formData.operator, rechargeType);
    }
  }, [formData.operator, formData.circle, rechargeType]);

  // Auto-set operator and mobile for Google Play
  useEffect(() => {
    if (rechargeType === 'google_play') {
      setFormData(prev => ({ ...prev, operator: 'google_play', mobile: 'GOOGLEPLAY' }));
    }
  }, [rechargeType]);

  const fetchOperators = async () => {
    try { const r = await rechargeAPI.getOperators(); setOperators(r.data); } catch {}
  };
  const fetchRecent = async () => {
    try { const r = await rechargeAPI.getRecharges(5); const d = r.data; setRecentRecharges(Array.isArray(d) ? d : (d?.data || [])); } catch {}
  };
  const fetchCommissionRates = async (operator, service) => {
    try {
      const r = await rechargeAPI.getCommissionRates(operator, service);
      setCommRates(r.data);
    } catch { setCommRates(null); }
  };
  const fetchLivePlans = async (operator, circle) => {
    setLoadingPlans(true); setLivePlans([]); setPlanTab('all');
    try {
      const r = await rechargeAPI.browsePlansLive(operator, circle);
      if (r.data?.status === 'success' && r.data.plans?.length > 0) setLivePlans(r.data.plans);
    } catch {} finally { setLoadingPlans(false); }
  };
  const fetchOffers = async () => {
    if (!formData.mobile || formData.mobile.length < 10 || !formData.operator) {
      toast.error('Enter a valid 10-digit number and select operator'); return;
    }
    setLoadingOffers(true); setLiveOffers([]);
    try {
      const r = await rechargeAPI.getOffersLive(formData.mobile, formData.operator);
      if (r.data?.status === 'success') {
        setLiveOffers(r.data.plans || []);
        if (r.data.plans?.length === 0) toast.info('No personalized offers found');
        else setOffersOpen(true);
      } else toast.error(r.data?.message || 'Failed');
    } catch { toast.error('Failed to fetch offers'); } finally { setLoadingOffers(false); }
  };

  const fetchDthPlans = async (operator) => {
    setLoadingPlans(true); setLivePlans([]); setPlanTab('all');
    try {
      const r = await rechargeAPI.browseDthPlans(operator);
      if (r.data?.status === 'success' && r.data.plans?.length > 0) setLivePlans(r.data.plans);
    } catch {} finally { setLoadingPlans(false); }
  };

  const fetchDthInfo = async () => {
    if (!formData.mobile || !formData.operator) { toast.error('Enter customer ID and select operator'); return; }
    setLoadingDthInfo(true); setDthInfo(null);
    try {
      const r = await rechargeAPI.getDthInfo(formData.mobile, formData.operator);
      if (r.data?.status === 'success') { setDthInfo(r.data.info?.data || r.data.info || []); setDthInfoOpen(true); }
      else toast.error(r.data?.message || 'Failed to fetch DTH info');
    } catch { toast.error('Failed to fetch DTH info'); } finally { setLoadingDthInfo(false); }
  };

  const fetchDthHeavy = async () => {
    if (!formData.mobile || !formData.operator) { toast.error('Enter customer ID and select operator'); return; }
    setRefreshingDth(true);
    try {
      const r = await rechargeAPI.getDthHeavyPlans(formData.mobile, formData.operator);
      if (r.data?.status === 'success') toast.success(`Heavy Refresh sent! ${r.data.customer_name ? 'Customer: ' + r.data.customer_name : ''}`);
      else toast.error(r.data?.message || 'Heavy refresh failed');
    } catch { toast.error('Heavy refresh failed'); } finally { setRefreshingDth(false); }
  };

  const detectOperator = useCallback(async (number) => {
    if (number.length !== 10 || rechargeType === 'dth') return;
    setDetecting(true);
    try {
      const r = await rechargeAPI.detectOperator(number);
      if (r.data?.detected) {
        setFormData(prev => ({ ...prev, operator: r.data.operator || prev.operator, circle: r.data.circle || prev.circle }));
        toast.success(`Detected: ${r.data.operator_name || r.data.operator}${r.data.circle ? ' - ' + r.data.circle : ''}`);
      }
    } catch {} finally { setDetecting(false); }
  }, [rechargeType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'operator') { setFormData(prev => ({ ...prev, [field]: value, amount: '' })); setLivePlans([]); setLiveOffers([]); }
    if (field === 'mobile' && value.replace(/\D/g, '').length === 10 && rechargeType !== 'dth') detectOperator(value.replace(/\D/g, ''));
  };

  const handlePlanSelect = (amount) => {
    setFormData(prev => ({ ...prev, amount: amount.toString() }));
    setPlansOpen(false); setOffersOpen(false);
  };

  const handleClear = () => {
    setFormData({ mobile: '', operator: '', circle: 'Delhi & NCR', amount: '' });
    setResult(null); setLivePlans([]); setLiveOffers([]);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (rechargeType === 'google_play') {
      if (!formData.amount) { toast.error('Enter redeem amount'); return; }
    } else {
      if (!formData.mobile || !formData.operator || !formData.amount) { toast.error('Please fill all fields'); return; }
      if (rechargeType !== 'dth' && formData.mobile.length < 10) { toast.error('Enter a valid 10-digit number'); return; }
    }
    const amt = parseFloat(formData.amount);
    if (amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (user.wallet_balance < amt) { toast.error('Insufficient wallet balance'); return; }
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false); setLoading(true); setResult(null);
    try {
      const response = await rechargeAPI.perform({
        mobile: formData.mobile, operator: formData.operator,
        circle: formData.circle, amount: parseFloat(formData.amount), recharge_type: rechargeType,
      });
      setResult(response.data);
      if (response.data.status === 'success') toast.success('Recharge successful!');
      else if (response.data.status === 'pending') toast.info('Processing...');
      else toast.error('Failed. Amount refunded.');
      const balanceRes = await walletAPI.getBalance();
      updateUser({ ...user, wallet_balance: balanceRes.data.balance });
      fetchRecent();
      if (rechargeType === 'google_play') {
        setFormData({ mobile: 'GOOGLEPLAY', operator: 'google_play', circle: 'All India', amount: '' });
      } else {
        setFormData({ mobile: '', operator: '', circle: 'Delhi & NCR', amount: '' });
      }
      setLivePlans([]); setLiveOffers([]);
    } catch (error) { toast.error(error.response?.data?.detail || 'Recharge failed'); }
    finally { setLoading(false); }
  };

  const currentOperators = rechargeType === 'dth' ? (operators.dth || []) : rechargeType === 'google_play' ? (operators.google_play || []) : (operators.prepaid || []);
  // Update page title
  const pageTitle = rechargeType === 'dth' ? 'DTH Recharge' : rechargeType === 'postpaid' ? 'Postpaid Recharge' : rechargeType === 'google_play' ? 'Google Play Recharge' : 'Mobile Recharge';
  const selectedOpName = currentOperators.find(o => o.id === formData.operator)?.name || '';
  const amount = parseFloat(formData.amount) || 0;
  const commRate = commRates ? commRates.retailer : (user?.retailer_commission || 2);
  const commType = commRates?.type || 'percentage';
  const commission = commType === 'flat' ? commRate : (amount * commRate) / 100;
  const planCategories = livePlans.map(p => p.name);
  const getPlansForTab = (tab) => {
    if (tab === 'all') return livePlans[0]?.subplan || [];
    return livePlans.find(p => p.name === tab)?.subplan || [];
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="recharge-page">

        {/* ===== HERO HEADER WITH ANIMATED GRADIENT ===== */}
        <div className="relative overflow-hidden rounded-3xl mb-6" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 30%, #4680ff 60%, #60a5fa 85%, #93c5fd 100%)',
          backgroundSize: '300% 300%',
          animation: 'gradientShift 8s ease infinite',
        }}>
          {/* Floating orbs */}
          <div className="rch-orb animate-orb1" style={{ width: 120, height: 120, background: 'rgba(129,140,248,0.2)', top: -20, right: '20%' }} />
          <div className="rch-orb animate-orb2" style={{ width: 80, height: 80, background: 'rgba(249,115,22,0.15)', bottom: -10, left: '15%' }} />
          <div className="rch-orb animate-orb1" style={{ width: 60, height: 60, background: 'rgba(236,72,153,0.12)', top: '40%', right: '5%' }} />

          <div className="relative z-10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="animate-slide-in-left">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center animate-float">
                  <Zap className="w-5 h-5 text-yellow-300" />
                </div>
                <span className="text-white/60 text-xs font-semibold uppercase tracking-widest">Quick Recharge</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                {pageTitle}
              </h1>
              <p className="text-white/40 text-sm mt-1">Fast, secure, and instant</p>
            </div>

            <div className="flex items-center gap-3" style={{ animation: 'slideInRight 0.5s ease-out 0.2s both' }}>
              {/* Wallet Balance Chip */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/10">
                <p className="text-white/50 text-[10px] font-semibold uppercase tracking-wider">Balance</p>
                <p className="text-xl font-extrabold text-white font-mono" data-testid="wallet-balance">
                  &#8377;{user?.wallet_balance?.toLocaleString('en-IN') || '0'}
                </p>
              </div>
              {/* Commission Chip */}
              <div className="bg-emerald-500/15 backdrop-blur-md rounded-2xl px-5 py-3 border border-emerald-400/20">
                <p className="text-emerald-300/60 text-[10px] font-semibold uppercase tracking-wider">Commission</p>
                <p className="text-xl font-extrabold text-emerald-300 font-mono" data-testid="est-commission">
                  +&#8377;{commission.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Banner Slider */}
        <BannerSlider pageType={rechargeType === 'dth' ? 'dth' : 'recharge'} />

        {/* ===== RESULT CARD (animated) ===== */}
        {result && (
          <div className={`mb-6 rounded-2xl border-2 p-5 animate-bounce-in ${
            result.status === 'success' ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50' :
            result.status === 'pending' ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50' :
            'border-red-300 bg-gradient-to-r from-red-50 to-pink-50'
          }`} data-testid="recharge-result">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                result.status === 'success' ? 'bg-emerald-100' : result.status === 'pending' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                {result.status === 'success' ? <CheckCircle className="w-7 h-7 text-emerald-600" /> : result.status === 'pending' ? <Clock className="w-7 h-7 text-amber-600" /> : <XCircle className="w-7 h-7 text-red-600" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{result.status === 'success' ? 'Recharge Successful!' : result.status === 'pending' ? 'Processing...' : 'Recharge Failed'}</h3>
                <p className="text-sm text-slate-500 font-mono">{result.mobile} &middot; &#8377;{result.amount} &middot; {result.operator}</p>
                <p className="text-xs text-slate-400 font-mono mt-0.5">TXN: {result.txn_id}</p>
                {result.status === 'success' && result.operator_id && (
                  <div className={`mt-2 px-3 py-2 rounded-lg border ${rechargeType === 'google_play' ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase">{rechargeType === 'google_play' ? 'Google Redeem Code' : 'Operator ID'}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold font-mono text-slate-800 select-all">{result.operator_id}</p>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(result.operator_id); toast.success('Copied!'); }}
                        className="p-1 rounded-md hover:bg-slate-200 transition-colors"
                        data-testid="copy-operator-id"
                        title="Copy"
                      >
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ===== MAIN FORM CARD ===== */}
          <div className="lg:col-span-2 rch-card-enter rch-delay-1">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-glow" style={{ animationDelay: '1s' }}>
              <div className="p-6 sm:p-8">
                {/* Type Toggle - Only show for mobile recharge, not DTH */}
                {rechargeType !== 'dth' && rechargeType !== 'google_play' && (
                <div className="flex items-center gap-3 mb-8">
                  {[
                    { type: 'prepaid', icon: Smartphone, label: 'Prepaid', gradient: 'from-[#4680ff] to-[#2563eb]' },
                    { type: 'postpaid', icon: Smartphone, label: 'Postpaid', gradient: 'from-orange-400 to-pink-500' },
                  ].map(({ type, icon: Icon, label, gradient }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { setRechargeType(type); setFormData(prev => ({ ...prev, operator: '', amount: '' })); setLivePlans([]); setLiveOffers([]); }}
                      className={`rch-type-btn flex items-center gap-2.5 px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-300 ${
                        rechargeType === type
                          ? `bg-gradient-to-r ${gradient} text-white shadow-lg scale-105`
                          : 'bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                      }`}
                      data-testid={`tab-${type}`}
                    >
                      <Icon className="w-5 h-5" />
                      {label}
                    </button>
                  ))}
                </div>
                )}

                <form onSubmit={handleConfirm} className="space-y-6">
                  {/* Recharge Number - hidden for Google Play */}
                  {rechargeType !== 'google_play' && (
                  <div className={`rch-card-enter rch-delay-2 transition-all duration-300 ${focusedField === 'mobile' ? 'scale-[1.01]' : ''}`}>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      <Signal className="w-3.5 h-3.5 text-blue-400" />
                      {rechargeType === 'dth' ? 'Customer ID' : 'Mobile Number'}
                    </label>
                    <div className="relative rch-input rounded-2xl border-2 border-slate-100 overflow-hidden transition-all duration-300">
                      <Input
                        placeholder={rechargeType === 'dth' ? 'Enter customer ID' : 'Enter 10-digit mobile number'}
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        onFocus={() => setFocusedField('mobile')}
                        onBlur={() => setFocusedField(null)}
                        maxLength={rechargeType === 'dth' ? 15 : 10}
                        className="h-14 border-0 bg-transparent text-base font-mono pl-4 pr-12 focus:ring-0 focus:border-0 shadow-none"
                        data-testid="recharge-mobile-input"
                      />
                      {detecting ? (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                        </div>
                      ) : formData.mobile.length === 10 && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 animate-scale-in">
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        </div>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Operator - hidden for Google Play */}
                  {rechargeType !== 'google_play' && (
                  <div className={`rch-card-enter rch-delay-3 transition-all duration-300 ${focusedField === 'operator' ? 'scale-[1.01]' : ''}`}>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                      Service Provider
                    </label>
                    <div className="rch-input rounded-2xl border-2 border-slate-100 overflow-hidden transition-all duration-300">
                      <Select value={formData.operator} onValueChange={(v) => { handleInputChange('operator', v); setFocusedField(null); }} onOpenChange={(open) => setFocusedField(open ? 'operator' : null)}>
                        <SelectTrigger className="h-14 border-0 bg-transparent text-base rounded-2xl shadow-none focus:ring-0" data-testid="recharge-operator-select">
                          <SelectValue placeholder="Choose your operator" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          {currentOperators.map((op) => (
                            <SelectItem key={op.id} value={op.id} className="py-3 text-sm font-medium">{op.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  )}

                  {/* Circle */}
                  {(rechargeType === 'prepaid' || rechargeType === 'postpaid') && (
                    <div className="rch-card-enter rch-delay-3">
                      <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                        <MapPin className="w-3.5 h-3.5 text-pink-400" />
                        Circle / Zone
                      </label>
                      <div className="rch-input rounded-2xl border-2 border-slate-100 overflow-hidden transition-all duration-300">
                        <Select value={formData.circle} onValueChange={(v) => handleInputChange('circle', v)}>
                          <SelectTrigger className="h-14 border-0 bg-transparent text-base rounded-2xl shadow-none focus:ring-0" data-testid="recharge-circle-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl max-h-60">
                            {CIRCLES.map((c) => (
                              <SelectItem key={c} value={c} className="py-2.5 text-sm">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Amount */}
                  <div className={`rch-card-enter rch-delay-4 transition-all duration-300 ${focusedField === 'amount' ? 'scale-[1.01]' : ''}`}>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      <IndianRupee className="w-3.5 h-3.5 text-emerald-400" />
                      {rechargeType === 'google_play' ? 'Redeem Amount' : 'Recharge Amount'}
                    </label>
                    <div className="relative rch-input rounded-2xl border-2 border-slate-100 overflow-hidden transition-all duration-300">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-300">&#8377;</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="0"
                        value={formData.amount}
                        onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); handleInputChange('amount', v); }}
                        onFocus={() => setFocusedField('amount')}
                        onBlur={() => setFocusedField(null)}
                        className="pl-10 h-14 border-0 bg-transparent text-2xl font-extrabold font-mono focus:ring-0 shadow-none"
                        data-testid="recharge-amount-input"
                      />
                    </div>
                  </div>

                  {/* Quick Amounts */}
                  <div className="rch-card-enter rch-delay-4">
                    <div className="flex flex-wrap gap-2">
                      {(rechargeType === 'google_play' ? [10, 30, 50, 75, 100, 150, 200, 300, 500] : [49, 99, 149, 199, 249, 299, 399, 499, 599]).map((amt, i) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, amount: amt.toString() }))}
                          className={`px-4 py-2 rounded-xl text-xs font-bold font-mono transition-all duration-200 ${
                            formData.amount === amt.toString()
                              ? 'bg-gradient-to-r from-[#4680ff] to-[#2563eb] text-white shadow-md shadow-blue-400/30 scale-105'
                              : 'bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-[#4680ff] hover:scale-105'
                          }`}
                          style={{ animationDelay: `${i * 0.03}s` }}
                          data-testid={`quick-amt-${amt}`}
                        >
                          &#8377;{amt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* View Plan / View R-Offer / DTH Actions - hidden for Google Play */}
                  {rechargeType !== 'google_play' && (
                  <div className="flex items-center gap-5 flex-wrap rch-card-enter rch-delay-5">
                    <button
                      type="button"
                      onClick={() => { if (!formData.operator) { toast.error('Select an operator first'); return; } setPlansOpen(true); }}
                      className="group flex items-center gap-2 text-sm font-bold text-[#4680ff] hover:text-[#2563eb] transition-all duration-200"
                      data-testid="view-plan-link"
                    >
                      <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      View Plan
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </button>
                    {(rechargeType === 'prepaid' || rechargeType === 'postpaid') && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!formData.operator || !formData.mobile || formData.mobile.length < 10) { toast.error('Enter number and operator first'); return; }
                          fetchOffers();
                        }}
                        className="group flex items-center gap-2 text-sm font-bold text-orange-500 hover:text-orange-700 transition-all duration-200"
                        data-testid="view-roffer-link"
                      >
                        <Gift className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        View R-Offer
                        {loadingOffers && <Loader2 className="w-3 h-3 animate-spin" />}
                      </button>
                    )}
                    {rechargeType === 'dth' && (
                      <>
                        <button
                          type="button"
                          onClick={fetchDthInfo}
                          disabled={loadingDthInfo}
                          className="group flex items-center gap-2 text-sm font-bold text-cyan-600 hover:text-cyan-800 transition-all duration-200"
                          data-testid="dth-info-link"
                        >
                          <User className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          DTH Info
                          {loadingDthInfo && <Loader2 className="w-3 h-3 animate-spin" />}
                        </button>
                        <button
                          type="button"
                          onClick={fetchDthHeavy}
                          disabled={refreshingDth}
                          className="group flex items-center gap-2 text-sm font-bold text-rose-500 hover:text-rose-700 transition-all duration-200"
                          data-testid="dth-heavy-link"
                        >
                          <RefreshCw className={`w-4 h-4 group-hover:scale-110 transition-transform ${refreshingDth ? 'animate-spin' : ''}`} />
                          Heavy Refresh
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 pt-3 rch-card-enter rch-delay-5">
                    <button
                      type="submit"
                      disabled={loading || (!formData.amount) || (rechargeType !== 'google_play' && (!formData.mobile || !formData.operator))}
                      className="rch-btn-gradient flex-1 h-14 rounded-2xl text-sm font-extrabold uppercase tracking-widest text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                      data-testid="recharge-submit-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                      ) : (
                        <><Send className="w-5 h-5" /> Proceed to Recharge</>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleClear}
                      className="h-14 px-6 rounded-2xl text-sm font-bold uppercase tracking-wider border-2 border-slate-200 text-slate-400 hover:border-red-200 hover:text-red-400 hover:bg-red-50/50 transition-all duration-300 flex items-center gap-2"
                      data-testid="recharge-clear-btn"
                    >
                      <RotateCcw className="w-4 h-4" /> Clear
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* ===== RIGHT SIDEBAR ===== */}
          <div className="space-y-5">
            {/* Recent Transactions */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden rch-card-enter rch-delay-2">
              <div className="p-5 pb-3 border-b border-slate-50">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  Recent Transaction
                </h3>
              </div>
              <div className="p-3">
                {recentRecharges.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-50 flex items-center justify-center">
                      <Info className="w-7 h-7 text-slate-200" />
                    </div>
                    <p className="text-xs text-slate-400 font-medium">Transaction not found!</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recentRecharges.map((r, i) => (
                      <div
                        key={r.id}
                        className="rch-txn-item flex items-center gap-3 p-3 rounded-2xl cursor-pointer"
                        onClick={() => { setFormData({ mobile: r.mobile, operator: r.operator, circle: r.circle || 'Delhi & NCR', amount: r.amount.toString() }); setRechargeType(r.recharge_type || 'prepaid'); }}
                        style={{ animationDelay: `${i * 0.1}s` }}
                        data-testid={`recent-recharge-${r.id}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          r.status === 'success' ? 'bg-gradient-to-br from-emerald-50 to-teal-100' :
                          r.status === 'pending' ? 'bg-gradient-to-br from-amber-50 to-yellow-100' :
                          'bg-gradient-to-br from-red-50 to-pink-100'
                        }`}>
                          {r.status === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : r.status === 'pending' ? <Clock className="w-5 h-5 text-amber-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-700 font-mono">{r.mobile}</p>
                          <p className="text-[11px] text-slate-400 capitalize">{r.operator}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-extrabold text-slate-800 font-mono">&#8377;{r.amount}</p>
                          <p className="text-[10px] text-slate-400">{r.created_at ? format(new Date(r.created_at), 'dd MMM') : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Commission Info Card */}
            {amount > 0 && (
              <div className="rounded-3xl overflow-hidden animate-scale-in" style={{
                background: 'linear-gradient(135deg, #ecfdf5, #d1fae5, #a7f3d0)',
              }}>
                <div className="p-5">
                  <p className="text-[10px] font-bold text-emerald-600/60 uppercase tracking-widest mb-2">Earning Preview</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-emerald-700/60">Recharge Amount</span>
                      <span className="text-sm font-bold font-mono text-emerald-800">&#8377;{amount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-emerald-700/60">Your Commission ({commType === 'flat' ? `₹${commRate}` : `${commRate}%`})</span>
                      <span className="text-lg font-extrabold font-mono text-emerald-600">+&#8377;{commission.toFixed(2)}</span>
                    </div>
                    <div className="h-px bg-emerald-300/30 my-1" />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-emerald-700/60">Net Cost</span>
                      <span className="text-sm font-bold font-mono text-emerald-800">&#8377;{(amount - commission).toFixed(2)}</span>
                    </div>
                    {commRates?.source === 'operator_specific' && (
                      <div className="mt-1">
                        <span className="text-[9px] bg-emerald-200/50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold" data-testid="operator-specific-badge">Operator-specific rate</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== PLANS DIALOG ===== */}
        <Dialog open={plansOpen} onOpenChange={setPlansOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden rounded-3xl border-0 shadow-2xl" data-testid="plans-dialog">
            <DialogHeader className="pb-2">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-[#4680ff] to-[#2563eb] flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                Plans — {selectedOpName || 'Select Operator'}
              </DialogTitle>
              <DialogDescription className="text-xs">{rechargeType === 'dth' ? 'Browse DTH plans' : `Browse plans from ${formData.circle}`}</DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh] -mx-2 px-2 pb-2">
              {loadingPlans ? (
                <div className="space-y-3 py-4">
                  {[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl rch-shimmer" />)}
                </div>
              ) : livePlans.length > 0 ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-4 sticky top-0 bg-white py-2 z-10">
                    {planCategories.map((cat, idx) => (
                      <button key={cat} type="button" onClick={() => setPlanTab(idx === 0 ? 'all' : cat)} className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${(idx === 0 ? planTab === 'all' : planTab === cat) ? 'bg-gradient-to-r from-[#4680ff] to-[#2563eb] text-white shadow-md' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>{cat}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {getPlansForTab(planTab).map((plan, i) => {
                      const hasDurations = rechargeType === 'dth' && plan.prices && plan.prices.length > 1;
                      if (hasDurations) {
                        return (
                          <div key={`${plan.validity}-${i}`} className="rch-plan-card p-4 rounded-2xl border-2 border-slate-100 text-left" data-testid={`dth-plan-${i}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-bold text-slate-800 truncate mr-2">{plan.validity || 'Plan'}</span>
                              <Badge className="bg-blue-50 text-[#4680ff] border-blue-200 text-[10px] rounded-lg shrink-0">Multi</Badge>
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-3">{plan.description}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {plan.prices.filter(p => parseFloat(p.amount) > 0).map((pr, j) => (
                                <button key={j} type="button" onClick={() => handlePlanSelect(pr.amount)}
                                  className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${formData.amount === pr.amount?.toString() ? 'border-blue-400 bg-blue-50 text-[#4680ff]' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50/50'}`}
                                  data-testid={`dth-price-${i}-${j}`}>
                                  <span className="font-mono">&#8377;{Math.round(parseFloat(pr.amount))}</span>
                                  <span className="text-slate-400 ml-1">/{pr.duration.replace(' MONTHS', 'mo').replace(' YEAR', 'yr').replace('1mo', '1M').replace('3mo', '3M').replace('6mo', '6M').replace('1yr', '1Y')}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <button key={`${plan.price}-${i}`} type="button" onClick={() => handlePlanSelect(plan.price)}
                          className={`rch-plan-card p-4 rounded-2xl border-2 text-left ${formData.amount === plan.price?.toString() ? 'border-blue-400 bg-blue-50/50 shadow-lg shadow-blue-100' : 'border-slate-100 hover:border-blue-200'}`}
                          data-testid={`plan-${plan.price}-${i}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xl font-extrabold font-mono bg-gradient-to-r from-[#4680ff] to-[#2563eb] bg-clip-text text-transparent">&#8377;{plan.price}</span>
                            {plan.validity && plan.validity !== 'N/A' && <Badge className="bg-blue-50 text-[#4680ff] border-blue-200 text-[10px] rounded-lg">{plan.validity}</Badge>}
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{plan.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-50 flex items-center justify-center">
                    <Search className="w-8 h-8 text-slate-200" />
                  </div>
                  <p className="text-sm text-slate-400 font-medium">No plans available</p>
                  <p className="text-xs text-slate-300 mt-1">Select an operator to browse plans</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== OFFERS DIALOG ===== */}
        <Dialog open={offersOpen} onOpenChange={setOffersOpen}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-hidden rounded-3xl border-0 shadow-2xl" data-testid="offers-dialog">
            <DialogHeader className="pb-2">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center">
                  <Gift className="w-4 h-4 text-white" />
                </div>
                R-Offers for {formData.mobile}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[55vh] -mx-2 px-2 pb-2">
              {liveOffers.length > 0 ? (
                <div className="space-y-3">
                  {liveOffers.map((offer, i) => (
                    <button key={i} type="button" onClick={() => handlePlanSelect(offer.amount)}
                      className="rch-plan-card w-full p-4 rounded-2xl border-2 border-orange-100 hover:border-orange-300 text-left bg-gradient-to-r from-orange-50/50 to-pink-50/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xl font-extrabold font-mono text-orange-600">&#8377;{offer.amount}</span>
                        <Badge className="bg-orange-100 text-orange-600 border-orange-200 text-[10px] rounded-lg">Offer</Badge>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{offer.desc}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-orange-50 flex items-center justify-center">
                    <Gift className="w-8 h-8 text-orange-200" />
                  </div>
                  <p className="text-sm text-slate-400">No R-Offers found</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== DTH INFO DIALOG ===== */}
        <Dialog open={dthInfoOpen} onOpenChange={setDthInfoOpen}>
          <DialogContent className="max-w-sm rounded-3xl border-0 shadow-2xl" data-testid="dth-info-dialog">
            <DialogHeader className="pb-2">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                DTH Customer Info
              </DialogTitle>
              <DialogDescription className="text-xs font-mono">{formData.mobile} — {selectedOpName}</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              {dthInfo && dthInfo.length > 0 ? (
                <div className="space-y-2.5">
                  {dthInfo.map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{item.label}</span>
                      <span className="text-sm font-bold text-slate-800">{item.value || '—'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-400">No info available</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== CONFIRM DIALOG ===== */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-sm rounded-3xl border-0 shadow-2xl" data-testid="confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                Confirm Recharge
              </DialogTitle>
              <DialogDescription className="text-xs">Verify all details before proceeding</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-100 space-y-3">
                <div className="flex justify-between text-sm"><span className="text-slate-400">{rechargeType === 'dth' ? 'Customer ID' : 'Mobile'}</span><span className="font-bold font-mono text-slate-800">{formData.mobile}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-400">Operator</span><span className="font-bold capitalize text-slate-800">{selectedOpName}</span></div>
                {rechargeType === 'prepaid' && <div className="flex justify-between text-sm"><span className="text-slate-400">Circle</span><span className="font-bold text-slate-800">{formData.circle}</span></div>}
                <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                <div className="flex justify-between items-center"><span className="font-semibold text-slate-600">Amount</span><span className="text-2xl font-extrabold font-mono bg-gradient-to-r from-[#4680ff] to-[#2563eb] bg-clip-text text-transparent">&#8377;{amount}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-400">Commission</span><span className="font-bold font-mono text-emerald-600">+&#8377;{commission.toFixed(2)}</span></div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-xl border-2 px-6">Cancel</Button>
              <button onClick={handleSubmit} className="rch-btn-gradient px-6 py-2.5 rounded-xl text-sm font-bold text-white" data-testid="confirm-recharge-btn">
                Confirm & Recharge
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Recharge;
